import { useState } from 'react'
import { apiFetch, saveToken } from '../lib/api'
import { defaultProfileForm, inputCls } from '../lib/constants'
import GoogleBtn from './GoogleBtn'
import ProfileFields from './ProfileFields'

export default function AuthModal({ onClose, onAuthenticated, gpsCity }) {
  const [view, setView]             = useState('login')
  const [signupStep, setSignupStep] = useState(1)
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState('')
  const [showPw, setShowPw]         = useState(false)

  const [loginEmail, setLoginEmail] = useState('')
  const [loginPw, setLoginPw]       = useState('')
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSuccess, setForgotSuccess] = useState('')
  const [devResetLink, setDevResetLink] = useState('')

  const [signupName, setSignupName]   = useState('')
  const [signupEmail, setSignupEmail] = useState('')
  const [signupPw, setSignupPw]       = useState('')
  const [signupPw2, setSignupPw2]     = useState('')
  const [otp, setOtp]                 = useState('')
  const [devOtp, setDevOtp]           = useState('')

  const [profileForm, setProfileForm] = useState({ ...defaultProfileForm, city: gpsCity || defaultProfileForm.city })

  // Read referral code stored when user arrived via referral link
  const pendingReferralCode = sessionStorage.getItem('coeater_ref') || ''

  function resetError() { setError('') }

  function resetForgotState() {
    setForgotSuccess('')
    setDevResetLink('')
  }

  async function handleLogin() {
    resetError(); setLoading(true)
    try {
      const res = await apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email: loginEmail, password: loginPw }) })
      const data = await res.json()
      if (!res.ok) { setError(data.message); return }
      saveToken(data.token)
      onAuthenticated(data.user)
    } catch { setError('Network error. Is the backend running?') }
    finally { setLoading(false) }
  }

  async function handleSendOtp() {
    resetError()
    if (!signupName.trim()) { setError('Name is required.'); return }
    if (!signupEmail.trim()) { setError('Email is required.'); return }
    if (signupPw.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (signupPw !== signupPw2) { setError('Passwords do not match.'); return }
    setLoading(true)
    try {
      const res = await apiFetch('/auth/send-otp', { method: 'POST', body: JSON.stringify({ email: signupEmail }) })
      const data = await res.json()
      if (!res.ok) { setError(data.message); return }
      if (data.devOtp) setDevOtp(data.devOtp)
      setSignupStep(2)
    } catch { setError('Network error.') }
    finally { setLoading(false) }
  }

  async function handleVerifyOtp() {
    resetError()
    if (otp.length !== 6) { setError('Enter the 6-digit OTP.'); return }
    setSignupStep(3)
  }

  async function handleSignup() {
    resetError()
    const birthYearNum = Number(profileForm.birthYear)
    const currentYear = new Date().getFullYear()
    if (!profileForm.bio.trim()) { setError('Bio is required.'); return }
    if (!profileForm.profilePicture.trim()) { setError('Profile picture is required.'); return }
    if (!profileForm.city.trim()) { setError('City is required.'); return }
    if (!profileForm.area.trim()) { setError('Area / neighbourhood is required.'); return }
    if (!profileForm.gender) { setError('Gender is required.'); return }
    if (!profileForm.whoWillPay) { setError('Who will pay is required.'); return }
    if (!Number.isInteger(birthYearNum) || birthYearNum < 1900 || birthYearNum > currentYear - 13) { setError('Enter a valid birth year.'); return }
    setLoading(true)
    try {
      const interests = profileForm.interestsInput.split(',').map(s => s.trim()).filter(Boolean)
      const res = await apiFetch('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          name: signupName, email: signupEmail, password: signupPw, otp,
          bio: profileForm.bio, profilePicture: profileForm.profilePicture,
          gender: profileForm.gender,
          birthYear: birthYearNum,
          location: { address: profileForm.address, city: profileForm.city, area: profileForm.area },
          preferences: {
            dietaryType: profileForm.dietaryType,
            budgetVibe: profileForm.budgetVibe,
            whoWillPay: profileForm.whoWillPay,
            interests,
          },
          availability: { isAvailable: profileForm.isAvailable, label: profileForm.availabilityLabel },
          referralCode: pendingReferralCode || undefined,
        })
      })
      const data = await res.json()
      if (!res.ok) {
        if (data.message?.toLowerCase().includes('otp')) setSignupStep(2)
        setError(data.message); return
      }
      sessionStorage.removeItem('coeater_ref')
        if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-18364286030/s2aPCJucotocEM6I47RE' // Replace with your Ads Conversion ID / Label
      });
    }
      saveToken(data.token)
      onAuthenticated(data.user)
    } catch { setError('Network error.') }
    finally { setLoading(false) }
  }

  async function handleGoogleCredential(credential) {
    resetError(); setLoading(true)
    try {
      const res = await apiFetch('/auth/google', {
        method: 'POST',
        body: JSON.stringify({ credential, referralCode: pendingReferralCode || undefined }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.message); return }
      sessionStorage.removeItem('coeater_ref')
      saveToken(data.token)
      onAuthenticated(data.user)
    } catch { setError('Network error.') }
    finally { setLoading(false) }
  }

  async function handleForgotPassword() {
    resetError()
    resetForgotState()
    if (!forgotEmail.trim()) { setError('Email is required.'); return }
    setLoading(true)
    try {
      const res = await apiFetch('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email: forgotEmail }) })
      const data = await res.json()
      if (!res.ok) { setError(data.message || 'Failed to send reset link.'); return }
      setForgotSuccess(data.message || 'If your email is registered, a reset link has been sent.')
      if (data.devResetLink) setDevResetLink(data.devResetLink)
    } catch { setError('Network error.') }
    finally { setLoading(false) }
  }

  const tabBtn = (id, label) => (
    <button onClick={() => { setView(id); resetError(); resetForgotState(); setSignupStep(1) }}
      className={`flex-1 py-2.5 text-sm font-bold rounded-xl transition ${view === id ? 'bg-orange-500 text-white shadow-md' : 'text-gray-600 hover:text-orange-600'}`}>
      {label}
    </button>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="glass-card w-full max-w-md rounded-3xl border border-white/60 p-8 shadow-2xl fade-up overflow-y-auto max-h-[95vh]">

        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="title-font text-xs font-bold uppercase tracking-[0.2em] text-orange-600">Co-Eater</p>
            <h2 className="title-font text-2xl font-bold text-gray-900 mt-0.5">
              {view === 'login' ? 'Welcome back 👋' : view === 'forgot' ? 'Reset password' : signupStep === 3 ? 'Your profile' : 'Create account'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">✕</button>
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
          {tabBtn('login',  '🔑 Login')}
          {tabBtn('signup', '✨ Sign Up')}
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {view === 'login' && (
          <div className="space-y-4">
            <input type="email" placeholder="Email address" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} className={inputCls} autoComplete="email" />
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} placeholder="Password" value={loginPw} onChange={e => setLoginPw(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()} className={inputCls + ' pr-12'} autoComplete="current-password" />
              <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg">{showPw ? '🙈' : '👁️'}</button>
            </div>
            <button onClick={handleLogin} disabled={loading} className="w-full rounded-full bg-gradient-to-r from-red-500 to-orange-500 py-3 text-sm font-bold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-50 transition">
              {loading ? 'Logging in…' : '🍽️ Login with Email'}
            </button>
            <button className="text-xs font-semibold text-orange-600 hover:underline text-left" onClick={() => { setForgotEmail(loginEmail); setView('forgot'); resetError(); resetForgotState() }}>
              Forgot password?
            </button>
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-gray-200" /><span className="text-xs text-gray-400 font-semibold">OR</span><div className="flex-1 h-px bg-gray-200" />
            </div>
            <GoogleBtn onCredential={handleGoogleCredential} label="signin_with" />
            <p className="text-center text-xs text-gray-500">
              No account?{' '}
              <button className="text-orange-600 font-semibold hover:underline" onClick={() => { setView('signup'); resetError() }}>Sign up free</button>
            </p>
          </div>
        )}

        {view === 'forgot' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Enter your account email and we will send a password reset link.</p>
            <input
              type="email"
              placeholder="Email address"
              value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
              className={inputCls}
              autoComplete="email"
            />
            <button
              onClick={handleForgotPassword}
              disabled={loading}
              className="w-full rounded-full bg-orange-500 py-3 text-sm font-bold text-white hover:bg-orange-600 disabled:opacity-50 transition"
            >
              {loading ? 'Sending link…' : 'Send reset link'}
            </button>
            {forgotSuccess && <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2">{forgotSuccess}</p>}
            {devResetLink && (
              <p className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-300 rounded-xl px-3 py-2 break-all">
                🔧 Dev reset link: <a href={devResetLink} className="font-semibold underline">Open link</a>
              </p>
            )}
            <button className="w-full text-center text-xs text-gray-500 hover:text-gray-700" onClick={() => { setView('login'); resetError(); resetForgotState() }}>
              ← Back to login
            </button>
          </div>
        )}

        {view === 'signup' && (
          <>
            <div className="flex gap-2 mb-6">
              {[1,2,3].map(n => (
                <div key={n} className="flex-1">
                  <div className={`h-1.5 rounded-full transition-all ${n <= signupStep ? 'bg-orange-500' : 'bg-gray-200'}`} />
                  <p className={`mt-1 text-center text-xs font-semibold ${n <= signupStep ? 'text-orange-600' : 'text-gray-400'}`}>
                    {n === 1 ? 'Account' : n === 2 ? 'Verify' : 'Profile'}
                  </p>
                </div>
              ))}
            </div>

            {signupStep === 1 && (
              <div className="space-y-3">
                <input placeholder="Full name *" value={signupName} onChange={e => setSignupName(e.target.value)} className={inputCls} />
                <input type="email" placeholder="Email address *" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} className={inputCls} autoComplete="email" />
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} placeholder="Password (min 8 chars) *" value={signupPw} onChange={e => setSignupPw(e.target.value)} className={inputCls + ' pr-12'} />
                  <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg">{showPw ? '🙈' : '👁️'}</button>
                </div>
                <input type="password" placeholder="Confirm password *" value={signupPw2} onChange={e => setSignupPw2(e.target.value)} className={inputCls} />
                <button onClick={handleSendOtp} disabled={loading} className="w-full rounded-full bg-orange-500 py-3 text-sm font-bold text-white hover:bg-orange-600 disabled:opacity-50 transition">
                  {loading ? 'Sending OTP…' : '📧 Send Verification Code'}
                </button>
                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-gray-200" /><span className="text-xs text-gray-400 font-semibold">OR</span><div className="flex-1 h-px bg-gray-200" />
                </div>
                <GoogleBtn onCredential={handleGoogleCredential} label="signup_with" />
              </div>
            )}

            {signupStep === 2 && (
              <div className="space-y-4">
                <div className="rounded-2xl bg-orange-50 border border-orange-200 px-4 py-3">
                  <p className="text-sm text-orange-800">We sent a 6-digit code to <strong>{signupEmail}</strong></p>
                  {devOtp && (
                    <p className="mt-2 text-xs font-mono font-bold bg-yellow-100 border border-yellow-300 text-yellow-800 rounded-lg px-3 py-2">
                      🔧 Dev OTP: <span className="text-lg tracking-widest">{devOtp}</span>
                    </p>
                  )}
                </div>
                <input
                  type="text" inputMode="numeric" maxLength={6} pattern="\d{6}"
                  placeholder="● ● ● ● ● ●"
                  value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={inputCls + ' text-center text-3xl tracking-[0.5em] font-bold'}
                  autoFocus
                />
                <button onClick={handleVerifyOtp} disabled={loading || otp.length !== 6}
                  className="w-full rounded-full bg-green-600 py-3 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50 transition">
                  ✅ Verify & Continue
                </button>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <button onClick={() => setSignupStep(1)} className="hover:text-orange-600">← Back</button>
                  <button onClick={handleSendOtp} disabled={loading} className="hover:text-orange-600 disabled:opacity-50">Resend OTP</button>
                </div>
              </div>
            )}

            {signupStep === 3 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Tell us about yourself so we can find the best dining matches.</p>
                <ProfileFields form={profileForm} setForm={setProfileForm} />
                <p className="text-xs leading-5 text-gray-500">
                  By tapping Submit, you agree to create an account and to CoEater's{' '}
                  <a href="/terms-of-use" target="_blank" rel="noreferrer" className="font-semibold text-orange-600 hover:underline">Terms of Use</a>
                  {' '}and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noreferrer" className="font-semibold text-orange-600 hover:underline">Privacy Policy</a>
                  .
                </p>
                <button onClick={handleSignup} disabled={loading}
                  className="w-full rounded-full bg-gradient-to-r from-red-500 to-orange-500 py-3 text-sm font-bold text-white hover:from-red-600 hover:to-orange-600 disabled:opacity-50 transition">
                  {loading ? 'Creating account…' : '🎉 Complete Signup'}
                </button>
                <button onClick={() => setSignupStep(2)} className="w-full text-center text-xs text-gray-400 hover:text-gray-600">← Back</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
